import { useEffect, useState } from "react";
import { api, money, formatApiErrorDetail } from "@/lib/api";
import { PageHeader } from "@/components/Layout";
import { useAuth } from "@/context/AuthContext";
import { useConfirm } from "@/components/ConfirmDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { ArrowDownToLine, ArrowUpFromLine, Wrench, Trash2, AlertTriangle, ScanBarcode, Warehouse, ArrowLeftRight, Building2, Plus, Pencil } from "lucide-react";

const TYPES = [
  { v: "received", label: "Stock Received", icon: ArrowDownToLine },
  { v: "issued", label: "Stock Issued", icon: ArrowUpFromLine },
  { v: "adjustment", label: "Adjustment (set warehouse total)", icon: Wrench },
  { v: "damaged", label: "Damaged Stock", icon: Trash2 },
];

const emptyMove = { product_id: "", type: "received", quantity: "", warehouse_id: "", note: "" };
const emptyTransfer = { product_id: "", from_warehouse_id: "", to_warehouse_id: "", quantity: "", note: "" };

export default function Inventory() {
  const { can } = useAuth();
  const confirm = useConfirm();
  const editable = can("inventory");
  const [movements, setMovements] = useState([]);
  const [low, setLow] = useState([]);
  const [products, setProducts] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [byWh, setByWh] = useState([]);
  const [open, setOpen] = useState(false);
  const [transferOpen, setTransferOpen] = useState(false);
  const [whOpen, setWhOpen] = useState(false);
  const [form, setForm] = useState(emptyMove);
  const [transfer, setTransfer] = useState(emptyTransfer);
  const [wh, setWh] = useState({ name: "", location: "" });
  const [whEditing, setWhEditing] = useState(null);
  const [scanCode, setScanCode] = useState("");

  const load = () => {
    api.get("/inventory/movements").then(({ data }) => setMovements(data));
    api.get("/inventory/low-stock").then(({ data }) => setLow(data));
    api.get("/products", { params: { include_inactive: true, gas: false } }).then(({ data }) => setProducts(data));
    api.get("/inventory/by-warehouse").then(({ data }) => setByWh(data));
  };
  useEffect(() => {
    load();
    api.get("/warehouses").then(({ data }) => {
      setWarehouses(data);
      if (data[0]) {
        setForm((f) => ({ ...f, warehouse_id: data[0].id }));
        setTransfer((t) => ({ ...t, from_warehouse_id: data[0].id, to_warehouse_id: data[1]?.id || "" }));
      }
    });
  }, []);

  const scan = async () => {
    const code = scanCode.trim();
    if (!code) return;
    let p = products.find((x) => x.barcode === code || (x.sku || "").toLowerCase() === code.toLowerCase());
    if (!p) {
      try { const { data } = await api.get(`/products/barcode/${encodeURIComponent(code)}`); p = data; }
      catch { return toast.error("No product for that barcode"); }
    }
    setForm((f) => ({ ...f, product_id: p.id }));
    setScanCode("");
    toast.success(`Selected ${p.name}`);
  };

  const save = async () => {
    try {
      await api.post("/inventory/movements", { ...form, quantity: Number(form.quantity) });
      toast.success("Stock movement recorded");
      setOpen(false);
      setForm({ ...emptyMove, warehouse_id: warehouses[0]?.id || "" });
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const doTransfer = async () => {
    try {
      await api.post("/inventory/transfer", { ...transfer, quantity: Number(transfer.quantity) });
      toast.success("Stock transferred");
      setTransferOpen(false);
      setTransfer({ ...emptyTransfer, from_warehouse_id: warehouses[0]?.id || "", to_warehouse_id: warehouses[1]?.id || "" });
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const saveWarehouse = async () => {
    if (!wh.name) return toast.error("Warehouse name required");
    try {
      if (whEditing) await api.put(`/warehouses/${whEditing}`, wh);
      else await api.post("/warehouses", wh);
      toast.success(whEditing ? "Warehouse updated" : "Warehouse added");
      setWhOpen(false);
      setWh({ name: "", location: "" });
      setWhEditing(null);
      const { data } = await api.get("/warehouses");
      setWarehouses(data);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const openEditWarehouse = (w) => {
    setWh({ name: w.warehouse_name, location: w.location || "" });
    setWhEditing(w.warehouse_id);
    setWhOpen(true);
  };

  const retireWarehouse = async (w) => {
    if (!(await confirm({ title: "Retire warehouse?", description: `"${w.warehouse_name}" will be removed. This is only allowed if it holds no stock — transfer stock out first.`, confirmText: "Retire" }))) return;
    try {
      await api.delete(`/warehouses/${w.warehouse_id}`);
      toast.success("Warehouse retired");
      const { data } = await api.get("/warehouses");
      setWarehouses(data);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <div>
      <PageHeader title="Inventory Control" subtitle="Real-time stock levels, warehouse transfers and alerts.">
        {editable && (
          <>
            <Button variant="outline" onClick={() => setTransferOpen(true)} data-testid="transfer-stock-button"><ArrowLeftRight className="h-4 w-4 mr-1.5" /> Transfer</Button>
            <Button onClick={() => setOpen(true)} className="bg-primary hover:bg-[#C81219] text-white transition-colors" data-testid="new-movement-button">Record Movement</Button>
          </>
        )}
      </PageHeader>

      <Tabs defaultValue="levels">
        <TabsList>
          <TabsTrigger value="levels" data-testid="tab-levels">Stock Levels</TabsTrigger>
          <TabsTrigger value="warehouses" data-testid="tab-warehouses">Warehouses ({warehouses.length})</TabsTrigger>
          <TabsTrigger value="alerts" data-testid="tab-alerts">Low Stock ({low.length})</TabsTrigger>
          <TabsTrigger value="history" data-testid="tab-history">Movement History</TabsTrigger>
        </TabsList>

        <TabsContent value="levels">
          <div className="bg-white border border-border rounded-md overflow-hidden mt-4">
            <Table>
              <TableHeader><TableRow className="bg-secondary hover:bg-secondary">
                <TableHead>SKU</TableHead><TableHead>Product</TableHead><TableHead>Category</TableHead>
                <TableHead className="text-right">Total On Hand</TableHead><TableHead className="text-right">Reorder</TableHead><TableHead>Status</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {products.map((p) => {
                  const lowFlag = Number(p.stock_quantity) <= Number(p.reorder_level);
                  return (
                    <TableRow key={p.id}>
                      <TableCell className="font-mono text-xs text-muted-foreground">{p.sku}</TableCell>
                      <TableCell className="font-medium">{p.name}</TableCell>
                      <TableCell className="text-xs text-muted-foreground">{p.category}</TableCell>
                      <TableCell className="text-right tabular font-semibold">{p.stock_quantity}</TableCell>
                      <TableCell className="text-right tabular text-muted-foreground">{p.reorder_level}</TableCell>
                      <TableCell>{lowFlag ? <Badge className="bg-primary text-white">Reorder</Badge> : <Badge variant="outline" className="border-green-600 text-green-700">OK</Badge>}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        <TabsContent value="warehouses">
          <div className="flex justify-end mt-4 mb-3">
            {editable && <Button size="sm" variant="outline" onClick={() => { setWh({ name: "", location: "" }); setWhEditing(null); setWhOpen(true); }} data-testid="add-warehouse-button"><Plus className="h-3.5 w-3.5 mr-1" /> Add Warehouse</Button>}
          </div>
          <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
            {byWh.map((w) => (
              <div key={w.warehouse_id} className="bg-white border border-border rounded-md overflow-hidden" data-testid={`warehouse-card-${w.warehouse_id}`}>
                <div className="p-4 border-b border-border">
                  <div className="flex items-center gap-2">
                    <div className="h-9 w-9 rounded-sm bg-black text-white flex items-center justify-center"><Warehouse className="h-4 w-4" /></div>
                    <div className="min-w-0">
                      <div className="font-display font-semibold leading-tight truncate">{w.warehouse_name}</div>
                      <div className="text-[11px] text-muted-foreground">{w.location}</div>
                    </div>
                    {editable && (
                      <div className="ml-auto flex gap-1">
                        <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => openEditWarehouse(w)} data-testid={`edit-warehouse-${w.warehouse_id}`}><Pencil className="h-3.5 w-3.5" /></Button>
                        <Button size="icon" variant="ghost" className="h-7 w-7 text-muted-foreground hover:text-primary" onClick={() => retireWarehouse(w)} data-testid={`retire-warehouse-${w.warehouse_id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                      </div>
                    )}
                  </div>
                  <div className="flex items-center justify-between mt-3 text-xs">
                    <span className="text-muted-foreground">{w.sku_count} SKUs</span>
                    <span className="font-semibold tabular">Value {money(w.total_value)}</span>
                  </div>
                </div>
                <div className="max-h-64 overflow-y-auto divide-y divide-border">
                  {w.items.length === 0 ? <p className="text-sm text-muted-foreground text-center py-6">No stock here yet.</p> : w.items.map((it) => (
                    <div key={it.product_id} className="flex items-center gap-2 px-4 py-2 text-sm">
                      <span className="font-mono text-[11px] text-muted-foreground w-16 shrink-0">{it.sku}</span>
                      <span className="flex-1 truncate">{it.name}</span>
                      <span className={`tabular font-semibold ${it.quantity <= it.reorder_level ? "text-primary" : ""}`}>{it.quantity}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="alerts">
          <div className="bg-white border border-border rounded-md p-5 mt-4">
            {low.length === 0 ? <p className="text-center text-muted-foreground py-8">All stock healthy.</p> : (
              <div className="divide-y divide-border">
                {low.map((p) => (
                  <div key={p.id} className="flex items-center gap-3 py-3">
                    <AlertTriangle className="h-4 w-4 text-primary" />
                    <span className="font-mono text-xs text-muted-foreground w-24">{p.sku}</span>
                    <span className="flex-1 font-medium">{p.name}</span>
                    <span className="text-sm text-muted-foreground">On hand: <b className="text-primary">{p.stock_quantity}</b> / reorder {p.reorder_level}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="history">
          <div className="bg-white border border-border rounded-md overflow-hidden mt-4">
            <Table>
              <TableHeader><TableRow className="bg-secondary hover:bg-secondary">
                <TableHead>Date</TableHead><TableHead>Product</TableHead><TableHead>Type</TableHead><TableHead>Warehouse</TableHead><TableHead>Reference</TableHead>
                <TableHead className="text-right">Qty</TableHead><TableHead className="text-right">Total Bal.</TableHead><TableHead>By</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {movements.map((m) => (
                  <TableRow key={m.id}>
                    <TableCell className="text-xs text-muted-foreground">{(m.created_at || "").slice(0, 16).replace("T", " ")}</TableCell>
                    <TableCell className="font-medium">{m.product_name || "—"}</TableCell>
                    <TableCell><Badge variant="outline" className="capitalize">{m.type}</Badge></TableCell>
                    <TableCell className="text-xs text-muted-foreground">{m.warehouse_name || "—"}</TableCell>
                    <TableCell className="font-mono text-xs text-muted-foreground">{m.reference || "—"}</TableCell>
                    <TableCell className="text-right tabular">{m.quantity}</TableCell>
                    <TableCell className="text-right tabular font-semibold">{m.balance_after}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">{m.user_name}</TableCell>
                  </TableRow>
                ))}
                {movements.length === 0 && <TableRow><TableCell colSpan={8} className="text-center py-10 text-muted-foreground">No movements yet.</TableCell></TableRow>}
              </TableBody>
            </Table>
          </div>
        </TabsContent>
      </Tabs>

      {/* Movement dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-display text-xl">Record Stock Movement</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wide text-muted-foreground flex items-center gap-1.5"><ScanBarcode className="h-3.5 w-3.5" /> Scan Barcode</Label>
              <Input value={scanCode} onChange={(e) => setScanCode(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); scan(); } }}
                placeholder="Scan or type barcode, then Enter" data-testid="movement-scan-input" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wide text-muted-foreground">Product</Label>
              <Select value={form.product_id} onValueChange={(v) => setForm({ ...form, product_id: v })}>
                <SelectTrigger data-testid="movement-product-select"><SelectValue placeholder="Select product" /></SelectTrigger>
                <SelectContent>{products.map((p) => <SelectItem key={p.id} value={p.id}>{p.name} ({p.stock_quantity})</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wide text-muted-foreground">Warehouse</Label>
                <Select value={form.warehouse_id} onValueChange={(v) => setForm({ ...form, warehouse_id: v })}>
                  <SelectTrigger data-testid="movement-warehouse-select"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>{warehouses.map((w) => <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wide text-muted-foreground">Type</Label>
                <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                  <SelectTrigger data-testid="movement-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{TYPES.map((t) => <SelectItem key={t.v} value={t.v}>{t.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wide text-muted-foreground">Quantity {form.type === "adjustment" && "(new warehouse total)"}</Label>
              <Input type="number" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} data-testid="movement-qty-input" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wide text-muted-foreground">Reference (optional)</Label>
              <Input value={form.reference || ""} onChange={(e) => setForm({ ...form, reference: e.target.value })} placeholder="e.g. GRN/PO number" data-testid="movement-reference-input" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wide text-muted-foreground">Note</Label>
              <Textarea value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} rows={2} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} className="bg-primary hover:bg-[#C81219] text-white transition-colors" data-testid="save-movement-button">Record</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Transfer dialog */}
      <Dialog open={transferOpen} onOpenChange={setTransferOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-display text-xl">Transfer Stock Between Warehouses</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wide text-muted-foreground">Product</Label>
              <Select value={transfer.product_id} onValueChange={(v) => setTransfer({ ...transfer, product_id: v })}>
                <SelectTrigger data-testid="transfer-product-select"><SelectValue placeholder="Select product" /></SelectTrigger>
                <SelectContent>{products.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wide text-muted-foreground">From</Label>
                <Select value={transfer.from_warehouse_id} onValueChange={(v) => setTransfer({ ...transfer, from_warehouse_id: v })}>
                  <SelectTrigger data-testid="transfer-from-select"><SelectValue placeholder="Source" /></SelectTrigger>
                  <SelectContent>{warehouses.map((w) => <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wide text-muted-foreground">To</Label>
                <Select value={transfer.to_warehouse_id} onValueChange={(v) => setTransfer({ ...transfer, to_warehouse_id: v })}>
                  <SelectTrigger data-testid="transfer-to-select"><SelectValue placeholder="Destination" /></SelectTrigger>
                  <SelectContent>{warehouses.map((w) => <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wide text-muted-foreground">Quantity</Label>
              <Input type="number" value={transfer.quantity} onChange={(e) => setTransfer({ ...transfer, quantity: e.target.value })} data-testid="transfer-qty-input" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wide text-muted-foreground">Note</Label>
              <Textarea value={transfer.note} onChange={(e) => setTransfer({ ...transfer, note: e.target.value })} rows={2} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTransferOpen(false)}>Cancel</Button>
            <Button onClick={doTransfer} className="bg-primary hover:bg-[#C81219] text-white transition-colors" data-testid="confirm-transfer-button">Transfer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Warehouse dialog */}
      <Dialog open={whOpen} onOpenChange={setWhOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle className="font-display text-xl flex items-center gap-2"><Building2 className="h-5 w-5 text-primary" /> {whEditing ? "Edit Warehouse" : "Add Warehouse"}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5"><Label className="text-xs uppercase tracking-wide text-muted-foreground">Name</Label><Input value={wh.name} onChange={(e) => setWh({ ...wh, name: e.target.value })} data-testid="warehouse-name-input" /></div>
            <div className="space-y-1.5"><Label className="text-xs uppercase tracking-wide text-muted-foreground">Location</Label><Input value={wh.location} onChange={(e) => setWh({ ...wh, location: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setWhOpen(false)}>Cancel</Button>
            <Button onClick={saveWarehouse} className="bg-primary hover:bg-[#C81219] text-white transition-colors" data-testid="save-warehouse-button">{whEditing ? "Save" : "Add"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
